import "./AddPaymentMethodPopup.scss";
import {useTranslation} from "react-i18next";
import React, {useEffect, useRef, useState} from "react";
import {useElements, useStripe} from "@stripe/react-stripe-js";
import {StripeCardCvcElement, StripeCardExpiryElement, StripeCardNumberElement} from "@stripe/stripe-js";
import {PaymentMethodService} from "../../services/paymentMethodService";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import {openMessage} from "../../store/notification/notificationSlice";
import CustomButton from "../UI/CustomButton/CustomButton";

interface AddPaymentMethodPopupProps {
    closePopupFn: () => void;
    getPaymentsMethodFn: (customerId: number) => Promise<void>;
}

const AddPaymentMethodPopup: React.FC<AddPaymentMethodPopupProps> = ({closePopupFn, getPaymentsMethodFn}) => {
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useAppDispatch();
    const {t} = useTranslation();
    const formRef = useRef(null);
    const inputRef = useRef<HTMLInputElement>(null)

    const {profile} = useAppSelector(state => state.profileSlice);
    const [isProcessing, setIsProcessing] = useState(false);

    let cardNumberElement: StripeCardNumberElement;
    let cardCvcElement: StripeCardCvcElement;
    let cardExpiryElement: StripeCardExpiryElement;

    useEffect(() => {
        showStripeElements()
    }, [stripe, elements])

    const showStripeElements = () => {
        if (!stripe || !elements) return;

        const cardNumberOptions = {
            style: getCardElementStyle(),
            placeholder: "Ex: 1234 1234 1234 1234"
        };
        const cardCvcOptions = {
            style: getCardElementStyle(),
            placeholder: "Ex: 123"
        };
        const cardExpiryOptions = {
            style: getCardElementStyle()
        };

        cardNumberElement = elements.create('cardNumber', cardNumberOptions);
        cardCvcElement = elements.create('cardCvc', cardCvcOptions);
        cardExpiryElement = elements.create('cardExpiry', cardExpiryOptions);

        cardNumberElement.mount('#card-number');
        cardCvcElement.mount('#card-cvc');
        cardExpiryElement.mount('#card-expiry');

    }

    const getCardElementStyle = () => {

        return {
            base: {
                fontFamily: "Roboto, sans-serif",
                fontSize: '17px',
                color: "#2D2D2D",
                lineHeight: "1.5714285714285714",
                "::placeholder": {
                    fontSize: "17px",
                    color: "rgba(45,45,45,0.35)",
                    lineHeight: "2.5714285714285714",
                    paddingTop: "10px",
                    marginTop: "10px"
                }
            }
        };
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        if (!stripe) return;
        setIsProcessing(true);
        const {token, error} = await stripe.createToken(cardNumberElement)
        if (error) {
            setIsProcessing(false);
            dispatch(openMessage({type: "error", title: t("FailedToAddedPaymentMethod")}));
            closePopupFn();
        } else {
            const paymentMethod = await stripe.createPaymentMethod({
                type: "card",
                card: {
                    token: token.id,
                },
                billing_details: {
                    name: inputRef.current?.value,
                },
            })
            if (paymentMethod && paymentMethod.paymentMethod) {
                await addPaymentMethod(paymentMethod.paymentMethod.id)
            }
            setIsProcessing(false);
        }
    }

    const addPaymentMethod = async (paymentMethodId: string) => {
        if (!profile) return;
        try {
            const response = await PaymentMethodService.addPaymentMethod(paymentMethodId, profile.customerId);
            if (response.status === 200) {
                getPaymentsMethodFn(profile.customerId);
                closePopupFn();
                dispatch(openMessage({
                    type: "success",
                    title: t("ThePaymentMethodWasAddedSuccessfully")
                }));
            }
        } catch (e) {
            closePopupFn()
            dispatch(openMessage({type: "error", title: t("FailedToAddedPaymentMethod")}));
        }

    }

    return (
        <div className={"add-payment-popup-container"}>
            <div className={"popup"}>
                <div className={"top"}>
                    <h3 className={"title"}>{t("AddNewCard")}</h3>
                </div>
                <div className={"content"}>
                    <h4 className={"content-title"}>{t("CardDetails")}</h4>
                    <form ref={formRef} id="payment-form">
                        <div className={"form-group"}>
                            <label htmlFor={"card-number"}>{t("CardNumber")}</label>
                            <div className={"stripe-input"} id="card-number"></div>
                        </div>
                        <div className={"form-row"}>
                            <div className={"form-group"}>
                                <label htmlFor={"card-expiry"}>{t("ExpirationDate")}</label>
                                <div className={"stripe-input"} id="card-expiry"></div>
                            </div>
                            <div className={"form-group"}>
                                <label htmlFor={"card-cvc"}>CVC</label>
                                <div className={"stripe-input"} id="card-cvc"></div>
                            </div>
                        </div>
                        <div className={"form-group"}>
                            <label htmlFor={"alias"}>{t("Alias")} <span>({t("Optional")})</span></label>
                            <input className={"stripe-input usual-input"}
                                   ref={inputRef}
                                   id={"alias"}
                                  />
                        </div>
                    </form>
                </div>
                <div className={"bottom"}>
                    <CustomButton
                        text={t("Cancel")}
                        type={"button"}
                        typeStyle={"outline"}
                        borderRadius={false}
                        blueForOutline={true}
                        style={{borderColor: "#FFF", backgroundColor: "transparent"}}
                        functionOnClick={closePopupFn}
                    />
                    <CustomButton
                        text={t("Save")}
                        type={"button"}
                        typeStyle={"fill"}
                        style={{borderRadius: "5px"}}
                        disabled={isProcessing}
                        borderRadius={false}
                        functionOnClick={(e: any) => handleSubmit(e)}
                    />
                </div>
            </div>
        </div>
    )
}

export default AddPaymentMethodPopup;